/** Phase 6 reads and writes: the vendor directory, its categories, and the
 * vendors on a booking (PHASE-6 §6.4).
 *
 * Every shape comes from the route contract, so a screen cannot drift from what
 * the Worker actually answers. Attaching a vendor invalidates both the booking's
 * list and the directory, because attaching is the one action that can change a
 * certificate's standing on a booking without touching the directory row.
 */
import { queryOptions } from "@tanstack/react-query";
import type { InferResponseType } from "hono/client";
import { readJson, rpc, type JsonBody, type QueryParams } from "./api";
import { queryClient } from "./session";

export type Vendor = InferResponseType<typeof rpc.api.vendors.$get>["items"][number];
export type VendorDetail = InferResponseType<typeof rpc.api.vendors[":id"]["$get"]>["item"];
export type VendorCategory = InferResponseType<typeof rpc.api["vendor-categories"]["$get"]>["items"][number];
export type VendorFilters = QueryParams<typeof rpc.api.vendors.$get>;
export type VendorBooking = InferResponseType<typeof rpc.api.vendors[":id"]["bookings"]["$get"]>["items"][number];
export type BookingVendor = InferResponseType<typeof rpc.api.bookings[":id"]["vendors"]["$get"]>["items"][number];

// ---------------------------------------------------------------- reads

export const vendorsQuery = (filters: VendorFilters = {}) => queryOptions({
  queryKey: ["vendors", filters],
  queryFn: () => readJson(rpc.api.vendors.$get({ query: filters })).then(result => result.items),
  staleTime: 60_000,
});

export const vendorQuery = (id: string) => queryOptions({
  queryKey: ["vendor", id],
  queryFn: () => readJson(rpc.api.vendors[":id"].$get({ param: { id } })).then(result => result.item),
  staleTime: 60_000,
});

export const vendorBookingsQuery = (id: string) => queryOptions({
  queryKey: ["vendor-bookings", id],
  queryFn: () => readJson(rpc.api.vendors[":id"].bookings.$get({ param: { id } })).then(result => result.items),
  staleTime: 60_000,
});

export const vendorCategoriesQuery = queryOptions({
  queryKey: ["vendor-categories"],
  queryFn: () => readJson(rpc.api["vendor-categories"].$get()).then(result => result.items),
  staleTime: 5 * 60_000,
});

/** Settings needs the switched-off ones too: a category that is merely hidden
 * from the filing picker still has vendors in it, and hiding it here as well
 * would make those vendors impossible to explain. */
export const allVendorCategoriesQuery = queryOptions({
  queryKey: ["vendor-categories", "all"],
  queryFn: () => readJson(rpc.api["vendor-categories"].$get({ query: { includeInactive: "1" } })).then(result => result.items),
  staleTime: 60_000,
});

export const bookingVendorsQuery = (bookingId: string) => queryOptions({
  queryKey: ["booking-vendors", bookingId],
  queryFn: () => readJson(rpc.api.bookings[":id"].vendors.$get({ param: { id: bookingId } })).then(result => result.items),
  staleTime: 30_000,
});

// ---------------------------------------------------------------- writes

const refreshDirectory = (id?: string) => Promise.all([
  queryClient.invalidateQueries({ queryKey: ["vendors"] }),
  ...(id ? [queryClient.invalidateQueries({ queryKey: ["vendor", id] })] : []),
]);

export const vendorDirectoryApi = {
  create: async (input: JsonBody<typeof rpc.api.vendors.$post>) => {
    const result = await readJson(rpc.api.vendors.$post({ json: input }));
    await refreshDirectory();
    return result.item;
  },
  update: async (id: string, patch: JsonBody<typeof rpc.api.vendors[":id"]["$patch"]>) => {
    const result = await readJson(rpc.api.vendors[":id"].$patch({ param: { id }, json: patch }));
    await refreshDirectory(id);
    return result.item;
  },
  setPreference: async (id: string, input: JsonBody<typeof rpc.api.vendors[":id"]["prefs"]["$post"]>) => {
    const result = await readJson(rpc.api.vendors[":id"].prefs.$post({ param: { id }, json: input }));
    await refreshDirectory(id);
    return result.item;
  },
  /** Multipart, because the certificate comes straight off a phone camera. */
  uploadCoi: async (id: string, file: File, expiresOn: string) => {
    const result = await readJson(rpc.api.vendors[":id"].coi.$post({ param: { id }, form: { file, expiresOn } }));
    await refreshDirectory(id);
    // A vendor's certificate changes what every booking it is on is waiting for.
    await queryClient.invalidateQueries({ queryKey: ["booking-vendors"] });
    return result.item;
  },
};

export const vendorCategoryApi = {
  create: async (input: JsonBody<typeof rpc.api["vendor-categories"]["$post"]>) => {
    const result = await readJson(rpc.api["vendor-categories"].$post({ json: input }));
    await queryClient.invalidateQueries({ queryKey: ["vendor-categories"] });
    return result.item;
  },
  update: async (id: string, patch: JsonBody<typeof rpc.api["vendor-categories"][":id"]["$patch"]>) => {
    const result = await readJson(rpc.api["vendor-categories"][":id"].$patch({ param: { id }, json: patch }));
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["vendor-categories"] }),
      queryClient.invalidateQueries({ queryKey: ["vendors"] }),
    ]);
    return result.item;
  },
};

const refreshBooking = (bookingId: string) => Promise.all([
  queryClient.invalidateQueries({ queryKey: ["booking-vendors", bookingId] }),
  queryClient.invalidateQueries({ queryKey: ["booking", bookingId] }),
]);

export const bookingVendorApi = {
  /** A one-off name typed straight onto the booking. */
  add: async (bookingId: string, input: JsonBody<typeof rpc.api.bookings[":id"]["vendors"]["$post"]>) => {
    const result = await readJson(rpc.api.bookings[":id"].vendors.$post({ param: { id: bookingId }, json: input }));
    await refreshBooking(bookingId);
    return result.item;
  },
  attach: async (bookingId: string, input: JsonBody<typeof rpc.api.bookings[":id"]["vendors"]["attach"]["$post"]>) => {
    const result = await readJson(rpc.api.bookings[":id"].vendors.attach.$post({ param: { id: bookingId }, json: input }));
    await refreshBooking(bookingId);
    return result.item;
  },
  createAndAttach: async (bookingId: string, input: JsonBody<typeof rpc.api.bookings[":id"]["vendors"]["new"]["$post"]>) => {
    const result = await readJson(rpc.api.bookings[":id"].vendors.new.$post({ param: { id: bookingId }, json: input }));
    await Promise.all([refreshBooking(bookingId), refreshDirectory()]);
    return result.item;
  },
  update: async (bookingId: string, vendorId: string, patch: JsonBody<typeof rpc.api.bookings[":id"]["vendors"][":vendorId"]["$patch"]>) => {
    const result = await readJson(rpc.api.bookings[":id"].vendors[":vendorId"].$patch({ param: { id: bookingId, vendorId }, json: patch }));
    await refreshBooking(bookingId);
    return result.item;
  },
  resync: async (bookingId: string, vendorId: string) => {
    const result = await readJson(rpc.api.bookings[":id"].vendors[":vendorId"].resync.$post({ param: { id: bookingId, vendorId } }));
    await refreshBooking(bookingId);
    return result.item;
  },
  reorder: async (bookingId: string, ids: string[]) => {
    const result = await readJson(rpc.api.bookings[":id"].vendors.order.$post({ param: { id: bookingId }, json: { ids } }));
    await refreshBooking(bookingId);
    return result.items;
  },
  remove: async (bookingId: string, vendorId: string) => {
    await readJson(rpc.api.bookings[":id"].vendors[":vendorId"].$delete({ param: { id: bookingId, vendorId } }));
    await refreshBooking(bookingId);
  },
};
